import { useState, useEffect } from 'react';
import { useCRMAuth } from '../../context/CRMAuthContext';
import axios from 'axios';
import { Plus, Mail, Shield, UserX, Key, Users, Crown, Briefcase, User } from 'lucide-react';
import { toast } from 'sonner';
import CRMLayout from '../../components/crm/CRMLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const roleIcons = { admin: Crown, manager: Briefcase, employee: User };
const roleColors = { 
  admin: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  manager: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  employee: 'bg-slate-500/10 text-slate-400 border-slate-500/20'
};

const CRMTeam = () => {
  const { token, isAdmin, company } = useCRMAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/crm/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data.users);
    } catch (error) { console.error(error); toast.error('Failed to load team'); }
    finally { setLoading(false); }
  };

  const deactivateUser = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await axios.delete(`${API}/api/crm/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User deactivated'); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const resetPassword = async (userId) => {
    const newPassword = prompt('Enter new password (min 8 chars):');
    if (!newPassword || newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try {
      await axios.post(`${API}/api/crm/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password reset');
    } catch { toast.error('Failed to reset password'); }
  };

  return (
    <CRMLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">Team Management</h1>
            <p className="text-slate-400">Manage your team members and roles</p>
          </div>
          {isAdmin() && (
            <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2">
              <Plus className="w-4 h-4" />Add User
            </button>
          )}
        </div>

        {/* Plan Info */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-slate-300">Team Members: <strong className="text-white">{users.filter(u => u.is_active).length}</strong> / {company?.max_users === 9999 ? 'Unlimited' : company?.max_users}</span>
          </div>
          <span className="text-sm text-slate-400 capitalize">{company?.subscription_plan} Plan</span>
        </div>

        {loading ? (
          <div className="flex justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div></div>
        ) : (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-400 uppercase">User</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-400 uppercase">Role</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-400 uppercase">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-slate-400 uppercase">Last Login</th>
                  {isAdmin() && <th className="text-right px-6 py-4 text-xs font-semibold text-slate-400 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {users.map((user) => {
                  const RoleIcon = roleIcons[user.role] || User;
                  return (
                    <tr key={user.user_id} className="hover:bg-slate-700/30">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium">
                            {user.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-white">{user.name}</p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${roleColors[user.role]}`}>
                          <RoleIcon className="w-3 h-3" />
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-400">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      {isAdmin() && (
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => resetPassword(user.user_id)} className="p-2 text-slate-400 hover:text-yellow-400 hover:bg-slate-700 rounded-lg" title="Reset Password">
                              <Key className="w-4 h-4" />
                            </button>
                            <button onClick={() => deactivateUser(user.user_id)} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded-lg" title="Deactivate">
                              <UserX className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && <AddUserModal token={token} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchUsers(); }} />}
    </CRMLayout>
  );
};

const AddUserModal = ({ token, onClose, onSuccess }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'employee', phone: '', job_title: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/crm/users`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User created!'); onSuccess();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create user'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>
      <div className="relative bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg">
        <div className="p-6 border-b border-slate-700"><h2 className="text-xl font-semibold text-white">Add Team Member</h2></div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-slate-300 mb-1">Full Name *</label>
            <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Email *</label>
            <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Password * (min 8 chars)</label>
            <input type="password" required minLength={8} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
          </div>
          <div>
            <label className="block text-sm text-slate-300 mb-1">Role *</label>
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white">
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Phone</label>
              <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Job Title</label>
              <input type="text" value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-slate-400">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">
              {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Plus className="w-4 h-4" />}
              Add User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CRMTeam;
